import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  FileText, 
  Briefcase, 
  Lock, 
  AlertOctagon, 
  Scale, 
  ShieldAlert, 
  Database, 
  Share2, 
  CheckCircle2, 
  XCircle, 
  MapPin, 
  Mail, 
  ExternalLink,
  Building2,
  Cpu,
  HelpCircle
} from 'lucide-react';

const TermsAndConditions = () => {
  const location = useLocation();

  const getNavClass = (path) => {
    const isActive = location.pathname === path;
    return `flex items-center px-5 py-3.5 rounded-2xl font-semibold transition-all duration-300 ${
      isActive 
        ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/25' 
        : 'text-gray-600 hover:bg-white/60 hover:text-gray-900 border border-transparent hover:shadow-sm'
    }`;
  };

  return (
    <div className="relative flex flex-col min-h-screen font-sans text-gray-800 bg-slate-100 md:flex-row overflow-hidden">
      
      {/* VIBRANT AMBIENT BACKGROUND ORBS */}
      <div className="absolute top-[-10%] left-[-5%] w-[45vw] h-[45vw] rounded-full bg-gradient-to-br from-blue-400/30 to-cyan-300/30 blur-[120px] pointer-events-none"></div>
      <div className="absolute bottom-[-10%] right-[-5%] w-[40vw] h-[40vw] rounded-full bg-gradient-to-tr from-indigo-400/25 to-purple-300/25 blur-[120px] pointer-events-none"></div>
      <div className="absolute top-[40%] left-[25%] w-[35vw] h-[35vw] rounded-full bg-gradient-to-r from-emerald-300/20 to-teal-300/20 blur-[100px] pointer-events-none"></div>

      {/* SHARED GLASS SIDEBAR */}
      <aside className="relative z-10 w-full bg-white/70 backdrop-blur-2xl border-b border-white/80 shadow-[4px_0_24px_rgba(0,0,0,0.03)] md:w-80 md:border-b-0 md:border-r md:min-h-screen flex flex-col">
        <div className="p-8 md:sticky md:top-0">
          <Link to="/" className="block mb-10 text-3xl font-black tracking-tighter text-gray-900 drop-shadow-sm">
            Expo<span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Lead</span>
          </Link>
          <nav className="flex gap-3 pb-4 overflow-x-auto md:flex-col md:pb-0">
            <Link to="/help" className={getNavClass('/help')}>Help Center</Link>
            <Link to="/privacy-policy" className={getNavClass('/privacy-policy')}>Privacy Policy</Link>
            <Link to="/terms-and-conditions" className={getNavClass('/terms-and-conditions')}>Terms of Service</Link>
            <Link to="/refund-policy" className={getNavClass('/refund-policy')}>Refund Policy</Link>
          </nav>
        </div>
      </aside>

      {/* MAIN CONTENT AREA */}
      <main className="relative z-10 flex-1 p-4 sm:p-8 md:p-12 lg:p-16 overflow-y-auto">
        <div className="max-w-5xl mx-auto bg-white/80 backdrop-blur-2xl border border-white shadow-2xl shadow-indigo-950/5 rounded-[2.5rem] p-6 sm:p-10 md:p-14 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500"></div>

          {/* HERO HEADER */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-6 mb-12 pb-8 border-b border-gray-100">
            <div className="flex items-center justify-center w-20 h-20 bg-gradient-to-br from-indigo-600 via-blue-600 to-cyan-600 rounded-3xl shadow-xl shadow-indigo-500/30 text-white transform hover:-rotate-6 transition-transform duration-300">
              <FileText className="w-11 h-11" />
            </div>
            <div>
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-200 mb-2">
                <Scale className="w-3.5 h-3.5 text-indigo-600" />
                Binding Enterprise Agreement
              </div>
              <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-gray-900">Terms of Service</h1>
              <p className="text-gray-500 font-medium mt-1">Effective Date: July 16, 2026 | Last Updated: July 16, 2026</p>
            </div>
          </div>

          <div className="space-y-12 text-gray-700">
            
            {/* 1. AGREEMENT TO TERMS (Interactive Highlight Card) */}
            <section className="p-8 bg-gradient-to-br from-indigo-500/10 sm:from-indigo-50 via-blue-50/50 to-purple-50/50 rounded-3xl border border-indigo-100/80 shadow-sm relative overflow-hidden group hover:shadow-md transition-all duration-300">
              <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-indigo-600 to-blue-600"></div>
              <h2 className="text-2xl font-black text-gray-900 mb-3 flex items-center gap-2.5">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-indigo-600 text-white text-sm font-bold shadow-md">1</span>
                Agreement to Terms
              </h2>
              <p className="text-lg leading-relaxed text-gray-700 pl-1 sm:pl-10">
                These Terms of Service ("Terms") constitute a legally binding agreement made between you ("User") and <strong>NSENGINEERS</strong> concerning your access to and use of the <strong>EXPOLEAD</strong> mobile application and website (<a href="https://expolead.thensgroup.in" target="_blank" rel="noreferrer" className="text-indigo-600 font-semibold hover:underline">https://expolead.thensgroup.in</a>). By downloading, installing, or accessing EXPOLEAD, you agree that you have read, understood, and agreed to be bound by all of these Terms.
              </p>
            </section>

            {/* 2. LICENSE & SERVICE DESCRIPTION */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-blue-100 text-blue-600 rounded-2xl shadow-sm"><Briefcase className="w-6 h-6" /></div>
                <h2 className="text-2xl font-black text-gray-900">2. Service Description & CRM License</h2>
              </div>
              <p className="text-gray-600 mb-6 leading-relaxed">
                EXPOLEAD provides business-to-business (B2B) sales enablement, optical character recognition (OCR) business card scanning, lead funnel tracking, and automated communication tools. 
              </p>
              <div className="p-6 rounded-2xl bg-white border border-gray-200/80 shadow-sm grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700"><strong>Granted Rights:</strong> We grant you a revocable, non-exclusive, non-transferable, limited license to use EXPOLEAD strictly for your internal B2B sales operations.</span>
                </div>
                <div className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700"><strong>Restrictions:</strong> You may not modify, reverse engineer, decompile, or extract the source code of the EXPOLEAD mobile client or its underlying AI/OCR models.</span>
                </div>
              </div>
            </section>

            {/* 3 & 4. ACCOUNT SECURITY & ACCEPTABLE USE GRID */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              
              <section className="p-8 rounded-3xl bg-white border border-gray-200/80 shadow-md hover:shadow-xl transition-all duration-300 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2.5 bg-purple-100 text-purple-600 rounded-xl"><Lock className="w-5 h-5" /></div>
                    <h2 className="text-xl font-bold text-gray-900">3. Account Security</h2>
                  </div>
                  <p className="text-gray-600 mb-6 leading-relaxed text-sm sm:text-base">
                    You must provide accurate, current, and complete business registration details when creating an account. You are entirely responsible for maintaining the confidentiality of your login credentials, session tokens, and OTPs.
                  </p>
                </div>
                <div className="p-4 rounded-2xl bg-purple-50/70 border border-purple-100 text-xs sm:text-sm text-purple-900 font-medium flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full bg-purple-600 flex-shrink-0"></span>
                  Notify us immediately at expolead@thensgroup.in upon learning of any unauthorized account breach.
                </div>
              </section>

              <section className="p-8 rounded-3xl bg-white border border-gray-200/80 shadow-md hover:shadow-xl transition-all duration-300 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2.5 bg-emerald-100 text-emerald-600 rounded-xl"><CheckCircle2 className="w-5 h-5" /></div>
                    <h2 className="text-xl font-bold text-gray-900">4. Acceptable Use Policy</h2>
                  </div>
                  <p className="text-gray-600 mb-6 leading-relaxed text-sm sm:text-base">
                    You agree to use EXPOLEAD lawfully. You are strictly prohibited from using the platform to harvest consumer personal data without consent, distribute unsolicited spam, or violate local telecom and anti-spam regulations.
                  </p>
                </div>
                <div className="p-4 rounded-2xl bg-emerald-50/70 border border-emerald-100 text-xs sm:text-sm text-emerald-900 font-medium flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full bg-emerald-600 flex-shrink-0"></span>
                  Users are strictly responsible for their outbound messaging compliance.
                </div>
              </section>

            </div>

            {/* 5. DATA OWNERSHIP & CRM LEADS (Interactive Comparison Cards) */}
            <section>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-cyan-100 text-cyan-600 rounded-2xl shadow-sm"><Database className="w-6 h-6" /></div>
                <div>
                  <h2 className="text-2xl font-black text-gray-900">5. Data Ownership & Intellectual Property</h2>
                  <p className="text-sm text-gray-500">Clear separation of rights between platform technology and your business CRM records.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-6 rounded-2xl bg-gradient-to-br from-cyan-50 to-white border border-cyan-100 shadow-sm hover:-translate-y-1 transition-all duration-300">
                  <span className="text-xs font-bold uppercase tracking-wider text-cyan-600 block mb-2">Your Ownership (The User)</span>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">CRM Leads & Scanned Contact Data</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    You retain 100% ownership of all business card images, prospect names, phone numbers, notes, and client profiles you scan or import into EXPOLEAD. We claim no intellectual property rights over your sales funnel data.
                  </p>
                </div>

                <div className="p-6 rounded-2xl bg-gradient-to-br from-indigo-50 to-white border border-indigo-100 shadow-sm hover:-translate-y-1 transition-all duration-300">
                  <span className="text-xs font-bold uppercase tracking-wider text-indigo-600 block mb-2">Our Ownership (NSENGINEERS)</span>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Platform Software & UI/UX Design</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    NSENGINEERS owns and retains all rights, title, and interest in the EXPOLEAD app, its logos, OCR scanning pipelines, backend algorithms, visual interfaces, and associated proprietary software code.
                  </p>
                </div>
              </div>
            </section>

            {/* 6. THIRD-PARTY SERVICES & META API (High Contrast Banner) */}
            <section className="p-8 rounded-3xl bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white shadow-xl relative overflow-hidden">
              <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-indigo-500/10 rounded-full blur-2xl"></div>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 bg-white/10 backdrop-blur-md rounded-xl text-cyan-400 border border-white/10"><Share2 className="w-6 h-6" /></div>
                <h2 className="text-2xl font-black">6. Third-Party Integrations & WhatsApp API</h2>
              </div>
              <p className="text-slate-300 text-base sm:text-lg leading-relaxed max-w-3xl mb-4">
                EXPOLEAD includes features that integrate with third-party communication tools, including the <strong className="text-white">Meta WhatsApp Cloud API</strong> and native mobile email/calling clients. 
              </p>
              <div className="p-4 rounded-xl bg-white/5 border border-white/10 text-sm text-slate-300">
                We are not responsible for the uptime, delivery rates, or policy enforcement of third-party platforms. Your use of Meta's messaging services through EXPOLEAD is subject to Meta's independent Business Messaging Terms.
              </div>
            </section>

            {/* 7 & 8. TERMINATION & LIMITATION OF LIABILITY */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <section className="p-8 rounded-3xl bg-gradient-to-br from-amber-50/80 to-orange-50/80 border border-amber-200/80 shadow-sm flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2.5 bg-amber-500 text-white rounded-xl shadow-md"><AlertOctagon className="w-5 h-5" /></div>
                    <h2 className="text-xl font-bold text-gray-900">7. Account Suspension</h2>
                  </div>
                  <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                    NSENGINEERS reserves the right to suspend or terminate your account access immediately, without prior notice or liability, if we determine that you have violated these Terms, engaged in fraudulent scanning activity, or abused our API infrastructure.
                  </p>
                </div>
              </section>

              <section className="p-8 rounded-3xl bg-gradient-to-br from-rose-50/80 to-pink-50/80 border border-rose-200/80 shadow-sm flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2.5 bg-rose-500 text-white rounded-xl shadow-md"><ShieldAlert className="w-5 h-5" /></div>
                    <h2 className="text-xl font-bold text-gray-900">8. Limitation of Liability</h2>
                  </div>
                  <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                    To the maximum extent permitted by applicable law, EXPOLEAD is provided "AS IS". NSENGINEERS shall not be liable for any indirect, incidental, special, or consequential damages, including loss of sales leads, business revenue, or data corruption.
                  </p>
                </div>
              </section>
            </div>

            {/* 9. GOVERNING LAW & JURISDICTION */}
            <section className="p-8 rounded-3xl bg-white border border-gray-200 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div className="flex items-start sm:items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-slate-100 text-slate-700 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-7 h-7" />
                </div>
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-gray-400 block">Section 9</span>
                  <h2 className="text-xl font-black text-gray-900">Governing Law & Jurisdiction</h2>
                  <p className="text-gray-600 text-sm sm:text-base mt-0.5">
                    These Terms shall be governed by and construed in accordance with the laws of India. Any legal disputes or claims arising out of your use of EXPOLEAD shall be subject to the exclusive jurisdiction of the competent courts located in <strong>Ahmedabad, Gujarat, India</strong>.
                  </p>
                </div>
              </div>
              <MapPin className="w-10 h-10 text-gray-300 hidden md:block flex-shrink-0" />
            </section>

            {/* 10. CONTACT & LEGAL SUPPORT BANNER */}
            <section className="p-8 sm:p-12 rounded-[2rem] bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-600 text-white shadow-2xl shadow-indigo-500/25 flex flex-col sm:flex-row items-center justify-between gap-8 text-center sm:text-left relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl pointer-events-none"></div>
              <div className="relative z-10">
                <span className="px-3 py-1 rounded-full bg-white/20 backdrop-blur-md text-xs font-bold uppercase tracking-wider mb-3 inline-block"></span>
                <h3 className="font-black text-3xl mb-2">Questions Regarding These Terms?</h3>
                <p className="text-blue-100 max-w-md text-base">Our legal and administrative support teams in Ahmedabad are available to address any licensing, compliance, or account inquiries.</p>
              </div>
              <a 
                href="mailto:expolead@thensgroup.in?subject=Terms%20of%20Service%20Inquiry" 
                className="relative z-10 px-8 py-4 font-black text-gray-900 transition-all bg-white rounded-2xl hover:bg-blue-50 shadow-xl hover:scale-105 active:scale-95 whitespace-nowrap text-lg flex items-center gap-2"
              >
                <Mail className="w-5 h-5 text-indigo-600" />
                expolead@thensgroup.in
              </a>
            </section>

          </div>

        </div>
      </main>
    </div>
  );
};

export default TermsAndConditions;